import { AuthContextProvider } from 'contexts/AuthContext';
import { FeatureFlagsProvider } from 'contexts/FeatureFlagsContext';
import { IdPConfigContextProvider } from 'contexts/IdPConfigContext';
import { ModeThemeProvider } from 'contexts/ModeThemeContext';
import ToastProvider from 'contexts/Toast/ToastProvider';
import LOCALES, { getCurrentLocaleMessages } from 'i18n/languages/locales';
import AppIntl from 'intl/AppIntl';
import React, { useMemo } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { IntlProvider } from 'react-intl';
import { QueryClientProvider } from 'react-query';
import {
	createBrowserRouter,
	createRoutesFromElements,
	Route,
	RouterProvider,
	useInRouterContext
} from 'react-router-dom';
import { DialogsContextProvider } from 'contexts/Dialogs/DialogsContext';
import { VerifiedDomainsProvider } from 'contexts/VerifiedDomainsCtx/VerifiedDomains';
import { generateQueryClient } from 'api/queryClient';
import { InternalRouterProvider } from 'contexts/InternalRouterCtx/InternalRouterCtx';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { env } from './Env/env';

// Emotion (via MUI) injects <style> elements at runtime, which a strict
// style-src rejects unless they carry the request nonce. DESCOPE_NONCE comes
// from the bootstrap script cspPlugin.js injects when ADD_CSP is on, and is
// undefined otherwise.
const styleCache = createCache({
	key: 'css',
	nonce: (window as any).DESCOPE_NONCE
});

const ProviderTree = ({
	children = null,
	queryClient
}: React.PropsWithChildren<{ queryClient: any }>) => (
	<CacheProvider value={styleCache}>
		<QueryClientProvider client={queryClient}>
			<HelmetProvider>
				<AuthContextProvider>
					<ModeThemeProvider>
						<ToastProvider>
							<FeatureFlagsProvider>
								<IntlProvider
									locale={LOCALES.en}
									messages={getCurrentLocaleMessages()}
								>
									<AppIntl />
									<IdPConfigContextProvider>
										<VerifiedDomainsProvider>
											<DialogsContextProvider>
												{children}
											</DialogsContextProvider>
										</VerifiedDomainsProvider>
									</IdPConfigContextProvider>
								</IntlProvider>
							</FeatureFlagsProvider>
						</ToastProvider>
					</ModeThemeProvider>
				</AuthContextProvider>
			</HelmetProvider>
		</QueryClientProvider>
	</CacheProvider>
);

const Providers = ({ children }: React.PropsWithChildren) => {
	const queryClient = useMemo(generateQueryClient, []);
	const isUnderRouter = useInRouterContext();

	const router = useMemo(
		() =>
			createBrowserRouter(
				createRoutesFromElements(
					<Route
						path="*"
						element={
							<InternalRouterProvider>
								<ProviderTree queryClient={queryClient}>
									{children}
								</ProviderTree>
							</InternalRouterProvider>
						}
					/>
				),
				{
					basename: env.baseRoute || undefined
				}
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	if (isUnderRouter) {
		return <ProviderTree queryClient={queryClient}>{children}</ProviderTree>;
	}

	return <RouterProvider router={router} />;
};

export default Providers;
